import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { Calendar, CheckCircle2, Clock, AlertCircle, PlayCircle, Edit, Shield, FileCheck, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { SprintFormDialog } from "@/components/sprints/sprint-form-dialog";
import { SprintActivityTimeline } from "@/components/sprints/sprint-activity-timeline";
import { ScreenshotsTabContent } from "@/components/sprints/screenshots-tab-content";
import { DocumentsTabContent } from "@/components/sprints/documents-tab-content";
import { WorkflowProgressBar } from "@/components/sprints/workflow-progress-bar";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { format } from "date-fns";
import { WORKFLOW_STAGES, STAGE_NUMBER_MAP, GATE_DEFINITIONS, GATE_STATUS_COLORS, type WorkflowStageValue } from "@/lib/constants/testing-constants";

interface Sprint {
  id: string;
  sprintNumber: string;
  sprintName: string | null;
  description: string | null;
  projectId?: string | null;
  projectName?: string | null;

  // 14-Stage Workflow State
  currentStage: WorkflowStageValue;
  stageStatus: "pending" | "in_progress" | "passed" | "blocked" | "completed" | "skipped" | "approved";
  stageLastUpdated: string;
  checkpoint: string | null;

  // Gate Tracking
  gate1Passed: boolean;
  gate1Artifact: string | null;
  gate1VerifiedAt: string | null;
  gate2Passed: boolean;
  gate2Artifact: string | null;
  gate2VerifiedAt: string | null;
  gate3Passed: boolean;
  gate3Artifact: string | null;
  gate3VerifiedAt: string | null;
  gate4Passed: boolean;
  gate4Artifact: string | null;
  gate4VerifiedAt: string | null;
  gate5Passed: boolean;
  gate5Artifact: string | null;
  gate5VerifiedAt: string | null;

  // Test Metrics
  testPassRate: string | null;
  testsPassing: number;
  testsTotal: number;
  blockersCount: number;

  // Evidence Artifacts
  evidenceArtifacts: any;

  // Progress Tracking
  tablesImplemented: string[];
  totalTables: number;
  completedTables: number;
  documentLinks: any;

  // Timeline
  startDate: string | null;
  endDate: string | null;

  // Audit
  createdAt: string;
  updatedAt: string;
}

interface TestChecklist {
  id: string;
  checklistNumber: string;
  title: string;
  sprintId: string;
  checklistType: "manual" | "automated";
  description: string | null;
  totalSteps: number;
  stepsCompleted: number;
  issuesFound: number;
  passRate: string;
  createdAt: string;
  updatedAt: string;
}

const STAGE_STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20", icon: PlayCircle },
  passed: { label: "Passed", color: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20", icon: CheckCircle2 },
  blocked: { label: "Blocked", color: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20", icon: AlertCircle },
  completed: { label: "Completed", color: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20", icon: CheckCircle2 },
  skipped: { label: "Skipped", color: "bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20", icon: Clock },
  approved: { label: "Approved", color: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20", icon: CheckCircle2 },
};

export default function SprintDetail() {
  const [, params] = useRoute("/project-manager/sprints/:id");
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useTabState("overview");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showTables, setShowTables] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: sprintData, isLoading } = useQuery<{ sprint: Sprint; checklists: TestChecklist[] }>({
    queryKey: ["/api/project-manager/sprints", params?.id],
    enabled: !!params?.id,
  });

  const sprint = sprintData?.sprint;

  // Use actual checklists data from API
  const checklists = sprintData?.checklists || [];
  const isLoadingChecklists = false;

  // Fetch sprint screenshots
  const { data: screenshotsData, isLoading: isLoadingScreenshots } = useQuery<{ screenshots: any[] }>({
    queryKey: ["/api/project-manager/sprints", params?.id, "screenshots"],
    enabled: !!params?.id,
  });

  const screenshots = screenshotsData?.screenshots || [];

  // Fetch sprint documents count
  const { data: documentsData } = useQuery<{ totalDocuments: number; documents: any[] }>({
    queryKey: ["/api/project-manager/sprints/by-number", sprint?.sprintNumber, "documents"],
    enabled: !!sprint?.sprintNumber,
  });

  const documentsCount = documentsData?.totalDocuments || 0;

  const handleNavItemClick = (href: string) => {
    const tabValue = href.replace("#", "");
    setActiveTab(tabValue);
  };

  const handleOpenChecklist = (checklistId: string) => {
    navigate(`/project-manager/testing-checklists/${checklistId}`);
  };

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/project-manager/sprints", params?.id] });
    setEditDialogOpen(false);
    toast({
      title: "Sprint updated",
      description: "Sprint has been updated successfully.",
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading sprint...</p>
      </div>
    );
  }

  if (!sprint) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">The sprint you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/project-manager/sprints")} size="sm">Back to Sprints</Button>
        </div>
      </div>
    );
  }

  // Calculate progress from tablesImplemented array length
  const implementedCount = sprint.tablesImplemented?.length || 0;
  const progress = sprint.totalTables > 0 ? Math.round((implementedCount / sprint.totalTables) * 100) : 0;

  // Calculate current stage number (needed for overall status calculation)
  const currentStageNumber = STAGE_NUMBER_MAP[sprint.currentStage] || 1;

  // Overall WORKFLOW status (not current stage status)
  // - Blocked: workflow halted at any stage
  // - Completed: ONLY when Stage 14 (currentStage === "complete") is marked completed
  // - Pending: Stage 1 hasn't started yet
  // - In Progress: working through stages 1-14
  const overallSprintStatus =
    sprint.stageStatus === "blocked" ? "blocked" :
    (sprint.currentStage === "complete" && sprint.stageStatus === "completed") ? "completed" :
    (currentStageNumber === 1 && sprint.stageStatus === "pending") ? "pending" :
    "in_progress";
  const statusInfo = STAGE_STATUS_CONFIG[overallSprintStatus] || STAGE_STATUS_CONFIG.in_progress;
  const StatusIcon = statusInfo.icon;
  const currentStageInfo = WORKFLOW_STAGES.find(s => s.number === currentStageNumber);

  // Get gate status
  const getGateStatus = (gateNumber: number): "passed" | "pending" | "failed" | "skipped" => {
    const gateMap: Record<number, boolean> = {
      1: sprint.gate1Passed,
      2: sprint.gate2Passed,
      3: sprint.gate3Passed,
      4: sprint.gate4Passed,
      5: sprint.gate5Passed,
    };
    const passed = gateMap[gateNumber];
    if (passed) return "passed";
    if (sprint.currentStage === "complete") return "skipped";
    return "pending";
  };

  const manualChecklists = checklists.filter(c => c.checklistType === "manual");
  const automatedChecklists = checklists.filter(c => c.checklistType === "automated");

  return (
    <>
    <DetailPageShell
      title={sprint.sprintNumber}
      subtitle={sprint.sprintName || undefined}
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Project Manager", href: "/project-manager" },
        { label: "Sprints", href: "/project-manager/sprints" },
        { label: sprint.sprintNumber },
      ]}
      value={activeTab}
      onValueChange={setActiveTab}
      onNavItemClick={handleNavItemClick}
      badge={
        <Badge variant="outline" className={statusInfo.color}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {statusInfo.label}
        </Badge>
      }
      extraContent={
        <WorkflowProgressBar
          stages={WORKFLOW_STAGES}
          currentStageNumber={currentStageNumber}
          currentStageComplete={sprint.stageStatus === 'completed'}
        />
      }
      navItems={[
        { label: "Overview", href: "#overview" },
        { label: "Workflow State", href: "#workflow" },
        { label: "Test Checklists", href: "#checklists", count: checklists.length },
        { label: "Screenshots", href: "#screenshots", count: screenshots.length },
        { label: "Activity", href: "#activity" },
        { label: "Documents", href: "#documents" },
      ]}
      linkedEntities={[
        { label: "Test Checklists", count: checklists.length, href: "#checklists" },
        { label: "Screenshots", count: screenshots.length, href: "#screenshots" },
        { label: "Tables", count: sprint.totalTables, href: "#overview" },
        { label: "Documents", count: documentsCount, href: "#documents" },
      ]}
      tabs={[
        {
          value: "overview",
          label: "Overview",
          content: (
            <div id="overview" className="space-y-2">
              {/* Sprint Details */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">SPRINT DETAILS</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Sprint Number</p>
                      <p className="text-sm font-medium">{sprint.sprintNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Sprint Name</p>
                      <p className="text-sm font-medium">{sprint.sprintName || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Status</p>
                      <Badge variant="outline" className={`${statusInfo.color} text-xs`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Project</p>
                      <p className="text-sm font-medium">{sprint.projectName || "-"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Description */}
              {sprint.description && (
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">DESCRIPTION</h3>
                    <p className="text-sm">{sprint.description}</p>
                  </CardContent>
                </Card>
              )}

              {/* Progress */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">PROGRESS</h3>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-muted-foreground">Tables Completed</span>
                        <span className="text-sm font-semibold">{implementedCount} / {sprint.totalTables}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{progress}% complete</p>
                    </div>

                    {sprint.tablesImplemented && sprint.tablesImplemented.length > 0 && (
                      <Collapsible open={showTables} onOpenChange={setShowTables}>
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between hover:bg-muted/50 p-2 rounded transition-colors cursor-pointer">
                            <p className="text-xs text-muted-foreground">
                              Implemented Tables ({sprint.tablesImplemented.length})
                            </p>
                            {showTables ? (
                              <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <div className="flex flex-wrap gap-1 pt-2 px-2 pb-2">
                            {sprint.tablesImplemented.map((table) => (
                              <Badge key={table} variant="secondary" className="text-xs">
                                {table}
                              </Badge>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">TIMELINE</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <p className="text-sm font-medium">
                          {sprint.startDate ? format(new Date(sprint.startDate), "MMM d, yyyy") : "-"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">End Date</p>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <p className="text-sm font-medium">
                          {sprint.endDate ? format(new Date(sprint.endDate), "MMM d, yyyy") : "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ),
        },
        {
          value: "workflow",
          label: "Workflow State",
          content: (
            <div id="workflow" className="space-y-2">
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">WORKFLOW STATE</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Current Stage</p>
                      <p className="text-sm font-medium">{currentStageInfo?.name || "-"} (Stage {currentStageNumber}/14)</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Stage Status</p>
                      <Badge variant="outline" className={statusInfo.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Checkpoint</p>
                      <p className="text-sm font-mono text-xs">{sprint.checkpoint || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
                      <p className="text-sm font-medium">
                        {sprint.stageLastUpdated ? format(new Date(sprint.stageLastUpdated), "MMM d, yyyy HH:mm") : "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 14-Stage Workflow Progress */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">14-STAGE WORKFLOW PROGRESS</h3>
                  <div className="space-y-2">
                    {WORKFLOW_STAGES.map((stage) => {
                      // Stage is complete if: before current stage, OR is current stage and status is 'completed'
                      const isComplete = stage.number < currentStageNumber ||
                                        (stage.number === currentStageNumber && sprint.stageStatus === 'completed');
                      const isCurrent = stage.number === currentStageNumber && sprint.stageStatus !== 'completed';

                      return (
                        <div key={stage.number}>
                          <div
                            className={`flex items-center gap-2 p-2 rounded ${
                              isCurrent ? "bg-primary/10" : isComplete ? "bg-muted/50" : ""
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                              isComplete ? "bg-green-500 text-white" :
                              isCurrent ? "bg-primary text-primary-foreground" :
                              "bg-muted text-muted-foreground"
                            }`}>
                              {stage.number}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isCurrent ? "font-semibold" : ""}`}>
                                  {stage.name}
                                </span>
                                {stage.gate && (
                                  <Badge variant="outline" className="text-xs">
                                    {stage.gate}
                                  </Badge>
                                )}
                              </div>
                              {isCurrent && (
                                <p className="text-xs text-muted-foreground mt-0.5">{stage.description}</p>
                              )}
                            </div>
                          </div>

                          {/* Show gate status if this stage has a gate */}
                          {stage.gate && stage.gate.startsWith("GATE-") && (
                            <div className="ml-8 mt-1 mb-2">
                              {(() => {
                                const gateNum = parseInt(stage.gate.match(/GATE-(\d+)/)?.[1] || "0");
                                if (gateNum === 0) return null;

                                const gateStatus = getGateStatus(gateNum);
                                const gateDef = GATE_DEFINITIONS[gateNum as keyof typeof GATE_DEFINITIONS];
                                const artifactMap: Record<number, string | null> = {
                                  1: sprint.gate1Artifact,
                                  2: sprint.gate2Artifact,
                                  3: sprint.gate3Artifact,
                                  4: sprint.gate4Artifact,
                                  5: sprint.gate5Artifact,
                                };
                                const verifiedAtMap: Record<number, string | null> = {
                                  1: sprint.gate1VerifiedAt,
                                  2: sprint.gate2VerifiedAt,
                                  3: sprint.gate3VerifiedAt,
                                  4: sprint.gate4VerifiedAt,
                                  5: sprint.gate5VerifiedAt,
                                };

                                return (
                                  <div className="flex items-start gap-2 p-2 border rounded-lg bg-background/50">
                                    <Shield className="h-3 w-3 mt-0.5 text-muted-foreground" />
                                    <div className="flex-1 text-xs">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium">{gateDef.name}</span>
                                        <Badge className={GATE_STATUS_COLORS[gateStatus]}>
                                          {gateStatus.toUpperCase()}
                                        </Badge>
                                      </div>
                                      <p className="text-muted-foreground">{gateDef.description}</p>
                                      {artifactMap[gateNum] && (
                                        <div className="mt-1 flex items-center gap-1">
                                          <FileCheck className="h-3 w-3" />
                                          <span className="font-mono text-xs">{artifactMap[gateNum]}</span>
                                        </div>
                                      )}
                                      {verifiedAtMap[gateNum] && (
                                        <p className="text-muted-foreground mt-0.5">
                                          Verified: {format(new Date(verifiedAtMap[gateNum]!), "MMM d, yyyy HH:mm")}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Test Metrics */}
              {(sprint.testsTotal > 0 || sprint.testPassRate) && (
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">TEST METRICS</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Pass Rate</p>
                        <p className={`text-sm font-semibold ${
                          sprint.testPassRate && parseFloat(sprint.testPassRate) >= 85 ? "text-green-600" :
                          sprint.testPassRate && parseFloat(sprint.testPassRate) >= 75 ? "text-yellow-600" :
                          "text-red-600"
                        }`}>
                          {sprint.testPassRate ? `${parseFloat(sprint.testPassRate).toFixed(1)}%` : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Tests Passing</p>
                        <p className="text-sm font-medium">{sprint.testsPassing} / {sprint.testsTotal}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Blockers</p>
                        <p className={`text-sm font-semibold ${sprint.blockersCount > 0 ? "text-red-600" : "text-green-600"}`}>
                          {sprint.blockersCount}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Gates Passed</p>
                        <p className="text-sm font-medium">
                          {[sprint.gate1Passed, sprint.gate2Passed, sprint.gate3Passed, sprint.gate4Passed, sprint.gate5Passed].filter(Boolean).length} / 5
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ),
        },
        {
          value: "checklists",
          label: `Test Checklists (${checklists.length})`,
          content: (
            <div id="checklists" className="space-y-2">
              {/* Automated Checklists */}
              {automatedChecklists.length > 0 && (
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-3">
                      AUTOMATED TESTS ({automatedChecklists.length})
                    </h3>
                    <div className="space-y-2">
                      {automatedChecklists.map((checklist) => {
                        const passRate = parseFloat(checklist.passRate);

                        return (
                          <div
                            key={checklist.id}
                            className="border rounded-lg p-3 hover:border-primary/50 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-semibold">{checklist.title}</h4>
                                  <Badge variant="secondary" className="text-xs">
                                    Automated
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {checklist.checklistNumber}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenChecklist(checklist.id)}
                              >
                                <PlayCircle className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            </div>

                            {checklist.description && (
                              <p className="text-xs text-muted-foreground mb-3">
                                {checklist.description}
                              </p>
                            )}

                            <div className="grid grid-cols-4 gap-3 text-xs">
                              <div>
                                <p className="text-muted-foreground">Total Steps</p>
                                <p className="font-semibold">{checklist.totalSteps}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Completed</p>
                                <p className="font-semibold">{checklist.stepsCompleted}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Issues</p>
                                <p className="font-semibold text-red-600">{checklist.issuesFound}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Pass Rate</p>
                                <p className={`font-semibold ${
                                  passRate >= 90 ? "text-green-600" :
                                  passRate >= 75 ? "text-yellow-600" :
                                  "text-red-600"
                                }`}>
                                  {passRate.toFixed(1)}%
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Manual Checklists */}
              {manualChecklists.length > 0 && (
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-3">
                      MANUAL TESTS ({manualChecklists.length})
                    </h3>
                    <div className="space-y-2">
                      {manualChecklists.map((checklist) => {
                        const passRate = parseFloat(checklist.passRate);

                        return (
                          <div
                            key={checklist.id}
                            className="border rounded-lg p-3 hover:border-primary/50 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-semibold">{checklist.title}</h4>
                                  <Badge variant="outline" className="text-xs">
                                    Manual
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {checklist.checklistNumber}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenChecklist(checklist.id)}
                              >
                                <PlayCircle className="h-3 w-3 mr-1" />
                                Run Test
                              </Button>
                            </div>

                            {checklist.description && (
                              <p className="text-xs text-muted-foreground mb-3">
                                {checklist.description}
                              </p>
                            )}

                            <div className="grid grid-cols-4 gap-3 text-xs">
                              <div>
                                <p className="text-muted-foreground">Total Steps</p>
                                <p className="font-semibold">{checklist.totalSteps}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Completed</p>
                                <p className="font-semibold">{checklist.stepsCompleted}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Issues</p>
                                <p className="font-semibold text-red-600">{checklist.issuesFound}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Pass Rate</p>
                                <p className={`font-semibold ${
                                  passRate >= 90 ? "text-green-600" :
                                  passRate >= 75 ? "text-yellow-600" :
                                  "text-red-600"
                                }`}>
                                  {checklist.totalSteps > 0 ? `${passRate.toFixed(1)}%` : "-"}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Empty State */}
              {checklists.length === 0 && !isLoadingChecklists && (
                <Card>
                  <CardContent className="pt-2">
                    <div className="text-center py-8">
                      <p className="text-sm text-muted-foreground">No test checklists available for this sprint</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Checklists will appear here once the sprint reaches Phase 6
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ),
        },
        {
          value: "screenshots",
          label: "Screenshots",
          content: (
            <div id="screenshots">
              <ScreenshotsTabContent
                sprintId={sprint.id}
                screenshots={screenshots}
                isLoading={isLoadingScreenshots}
              />
            </div>
          ),
        },
        {
          value: "activity",
          label: "Activity",
          content: (
            <div id="activity">
              <SprintActivityTimeline sprintId={sprint.id} />
            </div>
          ),
        },
        {
          value: "documents",
          label: "Documents",
          content: (
            <div id="documents">
              <DocumentsTabContent sprintNumber={sprint.sprintNumber} />
            </div>
          ),
        },
      ]}
      rightPanel={
        <div className="space-y-4">
          {/* Key Dates */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">
                  {sprint.createdAt ? format(new Date(sprint.createdAt), "MMM d, yyyy") : "-"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Modified</p>
                <p className="font-medium">
                  {sprint.updatedAt ? format(new Date(sprint.updatedAt), "MMM d, yyyy") : "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">QUICK STATS</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stage:</span>
                <span className="font-medium">{currentStageNumber}/14</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Progress:</span>
                <span className="font-medium">{Math.round((currentStageNumber / 14) * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gates Passed:</span>
                <span className="font-medium">
                  {[sprint.gate1Passed, sprint.gate2Passed, sprint.gate3Passed, sprint.gate4Passed, sprint.gate5Passed].filter(Boolean).length}/5
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tables:</span>
                <span className="font-medium">{implementedCount}/{sprint.totalTables}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Manual Checklists:</span>
                <span className="font-medium">{checklists.length}</span>
              </div>
              {/* Automated Test Pass Rate */}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Automated Tests:</span>
                {sprint.testPassRate ? (
                  <span className={`font-medium ${
                    parseFloat(sprint.testPassRate) >= 85 ? "text-green-600" :
                    parseFloat(sprint.testPassRate) >= 75 ? "text-yellow-600" :
                    "text-red-600"
                  }`}>
                    {parseFloat(sprint.testPassRate).toFixed(1)}%
                  </span>
                ) : (
                  <span className="font-medium text-muted-foreground">—</span>
                )}
              </div>
              {/* Manual Test Pass Rate */}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Manual Tests:</span>
                {(() => {
                  const manualChecklist = checklists.find(c => c.checklistType === "manual");
                  const manualPassRate = manualChecklist?.passRate ? parseFloat(manualChecklist.passRate) : null;

                  return manualPassRate !== null ? (
                    <span className={`font-medium ${
                      manualPassRate >= 85 ? "text-green-600" :
                      manualPassRate >= 75 ? "text-yellow-600" :
                      "text-red-600"
                    }`}>
                      {manualPassRate.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="font-medium text-muted-foreground">—</span>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACTIONS</h3>
            <div className="space-y-2">
              <Button
                size="sm"
                className="w-full justify-start text-xs"
                variant="outline"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="h-3 w-3 mr-2" />
                Edit Sprint
              </Button>
              <Button
                size="sm"
                className="w-full justify-start text-xs"
                variant="outline"
                onClick={() => navigate("/project-manager/sprints")}
              >
                Back to Sprints
              </Button>
            </div>
          </div>
        </div>
      }
      actions={
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/project-manager/sprints")}
          >
            Back to List
          </Button>
        </div>
      }
    />

      {/* Edit Sprint Dialog - must be outside DetailPageShell */}
      {sprint && (
        <SprintFormDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          sprint={{
            id: sprint.id,
            sprint_number: sprint.sprintNumber,
            sprint_name: sprint.sprintName,
            description: sprint.description,
            current_stage: sprint.currentStage,
            stage_status: sprint.stageStatus,
            project_id: sprint.projectId,
            start_date: sprint.startDate,
            end_date: sprint.endDate,
          }}
        />
      )}
    </>
  );
}
